// Advanced Longevity & Functional Medicine (Dr. Mavroson) —
// /advanced-longevity. NOT a Beluga visit; $199 for the first month, then
// $299/month. Modelled on the practice's Concierge Primary Care program
// (doctorskinnyshot.com/products/concierge-primary-care-program) with a
// functional-medicine intake: goals, symptoms, lifestyle, family history,
// prior therapies, labs and interest in advanced testing. Shared steps and
// helpers in ./conciergeCommon.js.
import {
  ageConfirmQuestion, stateQuestion, sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion,
  cancerQuestion, diabetesQuestion, conditionHistoryQuestion, conditionDetailsQuestion, previousTreatmentQuestions,
  medicationsQuestion, allergiesQuestion, additionalQuestionsQuestion, closingSteps,
  stateGate, applyCommonBranches, commonDisqualify, flaggedConditions,
} from './conciergeCommon';

export const ADVANCED_LONGEVITY_INITIAL_PRICE = 199;
export const ADVANCED_LONGEVITY_MONTHLY_PRICE = 299;
export const ADVANCED_LONGEVITY_PLAN_SLUG = 'advanced-longevity-99-199';
export const ADVANCED_LONGEVITY_NAME = 'Advanced Longevity & Functional Medicine';
export const ADVANCED_LONGEVITY_PRIMARY_GOAL = 'advancedLongevity';
export const LONGEVITY_DESCRIPTION =
  'A personalized, physician-guided membership for patients who want a comprehensive, functional-medicine approach to healthy aging. Members receive direct cell phone access to Dr. Mavroson, individualized evaluation and planning, functional medicine lab support, and preferred member pricing on advanced testing and select imaging services.';
export const LONGEVITY_INCLUDES = [
  'Direct cell phone access to Dr. Mavroson for text or calls',
  'Personalized longevity and functional-medicine evaluation and planning',
  'Full-scope telemedicine visits for eligible non-emergency concerns',
  'Comprehensive functional medicine lab evaluation with insurance lab partners',
  'Member pricing on advanced hormone (DUTCH) testing',
  'Member pricing on GI-Mapping, biological age testing (Epigenetics), microplastics testing, and other specialty lab services',
  'Preferred pricing on full-body MRI services',
  'Approximately 10% member savings on eligible medications',
  'Ongoing treatment management and clinical follow-up',
];
export const LONGEVITY_FOOTNOTE = 'Does not include the cost of medication or lab fees. Offered exclusively in New York.';
export const LONGEVITY_IMAGE = '/programs/concierge-longevity.jpg';

const introStep = {
  id: 'ap-intro', type: 'program-overview', question: 'Advanced Longevity & Functional Medicine',
  subtitle: 'Monthly membership · 100% online · Physician-guided healthy aging',
};
export const longevityGoalOptions = [
  { label: 'Healthy aging & longevity', value: 'healthy-aging' }, { label: 'More energy & vitality', value: 'energy' },
  { label: 'Deeper, more restorative sleep', value: 'sleep' }, { label: 'Mental clarity, focus & memory', value: 'cognition' },
  { label: 'Gut health & digestion', value: 'gut-health' }, { label: 'Hormone balance', value: 'hormones' },
  { label: 'Inflammation reduction', value: 'inflammation' }, { label: 'Immune support', value: 'immune' },
  { label: 'Body composition & metabolic health', value: 'metabolic' }, { label: 'Understanding my labs & biomarkers', value: 'labs' },
  { label: 'Other (please describe)', value: 'other' },
];
const goalsQuestion = { id: 'health-goals', type: 'multi-select', question: 'What are your top health goals right now?', subtitle: 'Select all that apply.', options: longevityGoalOptions };
const goalsOtherQuestion = { id: 'health-goals-other', type: 'textarea', question: 'Please describe your specific health goal', placeholder: 'Tell us what you want to achieve...' };
const symptomsQuestion = {
  id: 'current-symptoms', type: 'multi-select', question: 'Are you currently experiencing any of the following?', subtitle: 'Select all that apply',
  options: [
    { label: 'Fatigue or low energy', value: 'fatigue' }, { label: 'Brain fog or memory issues', value: 'brain-fog' },
    { label: 'Difficulty concentrating', value: 'difficulty-concentrating' }, { label: 'Low mood, anxiety or irritability', value: 'mood' },
    { label: 'Poor sleep', value: 'sleep' }, { label: 'Digestive issues (bloating, reflux, irregularity)', value: 'digestive' },
    { label: 'Joint pain or stiffness', value: 'joint-pain' }, { label: 'Weight gain that is hard to shift', value: 'weight-gain' },
    { label: 'Low libido or hormonal symptoms', value: 'hormonal' }, { label: 'Frequent illness or slow recovery', value: 'immune' },
    { label: 'Other', value: 'other' }, { label: 'None of these', value: 'none' },
  ],
};
const symptomsOtherQuestion = { id: 'current-symptoms-other', type: 'textarea', question: 'Please tell us more about your symptoms', placeholder: 'Describe what you notice, how long, and what makes it better or worse...' };
const sleepHoursQuestion = {
  id: 'sleep-hours', type: 'single-select', question: 'On average, how many hours of sleep do you get per night?',
  options: [{ label: 'Less than 5 hours', value: 'under-5' }, { label: '5–6 hours', value: '5-6' }, { label: '7–8 hours', value: '7-8' }, { label: 'More than 8 hours', value: 'over-8' }],
};
const sleepQualityQuestion = {
  id: 'sleep-quality', type: 'single-select', question: 'How would you rate your sleep quality?',
  options: [{ label: 'Poor', value: 'poor' }, { label: 'Fair', value: 'fair' }, { label: 'Good', value: 'good' }, { label: 'Excellent', value: 'excellent' }],
};
const exerciseQuestion = {
  id: 'exercise-frequency', type: 'single-select', question: 'How often do you exercise?',
  options: [{ label: 'Rarely or never', value: 'rarely' }, { label: '1–2 times a week', value: '1-2' }, { label: '3–4 times a week', value: '3-4' }, { label: '5 or more times a week', value: '5-plus' }],
};
const dietQuestion = {
  id: 'diet-quality', type: 'single-select', question: 'How would you describe your diet?',
  options: [{ label: 'Mostly whole foods', value: 'whole-foods' }, { label: 'Mixed — some processed foods', value: 'mixed' }, { label: 'Mostly processed or fast food', value: 'processed' }, { label: 'Following a specific plan (keto, fasting, plant-based…)', value: 'specific' }],
};
const stressQuestion = {
  id: 'chronic-stress', type: 'single-select', question: 'Do you experience chronic stress?',
  options: [{ label: 'Rarely', value: 'rarely' }, { label: 'Sometimes', value: 'sometimes' }, { label: 'Often', value: 'often' }, { label: 'Constantly', value: 'constantly' }],
};
const familyHistoryQuestion = {
  id: 'family-history', type: 'multi-select', question: 'Do any of these run in your immediate family?', subtitle: 'Parents or siblings. Select all that apply.',
  options: [
    { label: 'Heart disease or stroke', value: 'heart-disease' }, { label: 'Diabetes', value: 'diabetes' }, { label: 'Cancer', value: 'cancer' },
    { label: "Dementia or Alzheimer's", value: 'dementia' }, { label: 'Autoimmune disease', value: 'autoimmune' }, { label: 'Thyroid disease', value: 'thyroid' },
    { label: 'None of these', value: 'none' },
  ],
};
const [previousTreatmentQuestion, previousReactionQuestion, previousReactionDetailsQuestion] = previousTreatmentQuestions(
  'Have you tried functional-medicine, anti-aging or hormone therapies before?',
  'For example NAD+, peptides, hormone replacement, IV therapy, metformin or supplements protocols',
);
const lastLabQuestion = {
  id: 'last-lab-date', type: 'single-select', question: 'When was your last set of blood tests?',
  options: [{ label: 'Within the last 6 months', value: 'under-6-months' }, { label: '6–12 months ago', value: '6-12-months' }, { label: 'More than a year ago', value: 'over-1-year' }, { label: 'Never / not sure', value: 'never' }],
};
const abnormalResultsQuestion = {
  id: 'abnormal-results', type: 'single-select', question: 'Were any results flagged as abnormal?',
  options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }, { label: 'Not sure', value: 'unsure' }],
};
const abnormalDescribeQuestion = { id: 'abnormal-describe', type: 'textarea', question: 'Which results were flagged?', placeholder: 'e.g., LDL cholesterol high, vitamin D low, A1c 5.9...' };
const testingInterestQuestion = {
  id: 'testing-interest', type: 'multi-select', question: 'Which advanced testing are you interested in?', subtitle: 'Member pricing applies. Select all that apply.',
  options: [
    { label: 'Advanced hormone (DUTCH) testing', value: 'dutch' }, { label: 'GI-Mapping (gut microbiome)', value: 'gi-map' },
    { label: 'Biological age (epigenetics)', value: 'epigenetics' }, { label: 'Microplastics testing', value: 'microplastics' },
    { label: 'Full-body MRI', value: 'mri' }, { label: 'Comprehensive functional-medicine bloodwork', value: 'bloodwork' },
    { label: 'Not sure yet', value: 'unsure' },
  ],
};

export const advancedLongevityFlow = [
  introStep, goalsQuestion, goalsOtherQuestion, symptomsQuestion, symptomsOtherQuestion, ageConfirmQuestion, stateQuestion,
  sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion,
  sleepHoursQuestion, sleepQualityQuestion, exerciseQuestion, dietQuestion, stressQuestion,
  cancerQuestion, diabetesQuestion, conditionHistoryQuestion, conditionDetailsQuestion, familyHistoryQuestion,
  previousTreatmentQuestion, previousReactionQuestion, previousReactionDetailsQuestion,
  medicationsQuestion, allergiesQuestion,
  lastLabQuestion, abnormalResultsQuestion, abnormalDescribeQuestion, testingInterestQuestion, additionalQuestionsQuestion,
  ...closingSteps,
];
export const advancedLongevityFlowById = advancedLongevityFlow.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

export const buildDynamicAdvancedLongevityFlow = (answers = {}) => {
  const gated = stateGate(advancedLongevityFlow, answers);
  if (gated) return gated;
  let flow = applyCommonBranches([...advancedLongevityFlow], answers);
  const goals = answers['health-goals'];
  if (!Array.isArray(goals) || !goals.includes('other')) flow = flow.filter((q) => q.id !== 'health-goals-other');
  const symptoms = answers['current-symptoms'];
  if (!Array.isArray(symptoms) || !symptoms.includes('other')) flow = flow.filter((q) => q.id !== 'current-symptoms-other');
  if (answers['abnormal-results'] !== 'yes') flow = flow.filter((q) => q.id !== 'abnormal-describe');
  return flow;
};

// Hard stops: under 18, pregnant/breastfeeding, active cancer. Diabetes of any
// type is flagged, not stopped — functional medicine serves those patients.
export const isDisqualifyingAnswer = (questionId, answer) =>
  commonDisqualify(questionId, answer, { diabetesStops: [] });

export const longevityFlags = (answers = {}) => {
  const flags = flaggedConditions(answers);
  if (answers['abnormal-results'] === 'yes') flags.push('Abnormal prior labs');
  const fam = Array.isArray(answers['family-history']) ? answers['family-history'] : [];
  if (fam.includes('heart-disease')) flags.push('Family history: heart disease');
  if (fam.includes('cancer')) flags.push('Family history: cancer');
  return flags;
};

export default advancedLongevityFlow;
